/*
 * RR Header behavior — vanilla JS, no deps.
 * - Sticky-on-scroll shadow class
 * - Desktop mega-menu: hover open/close with 200ms close delay; keyboard open/close; ESC; click-outside
 * - Mobile drawer: open/close, body scroll lock, ESC, swipe-right close, focus trap
 * - Drawer "Shop" accordion: expand/collapse with chevron rotation
 * - prefers-reduced-motion respected throughout
 * - Runs on DOMContentLoaded (script is loaded with defer)
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // -------------------------------------------------------------------------
  // 1. Sticky header shadow on scroll
  // -------------------------------------------------------------------------
  const header = document.querySelector('[data-rr-header]');
  if (header) {
    // Expose header height as CSS custom prop so the mega menu can position under it
    const setHeaderHeight = () => {
      document.documentElement.style.setProperty('--rr-header-h', header.offsetHeight + 'px');
    };
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > 8) header.classList.add('is-scrolled');
          else header.classList.remove('is-scrolled');
          ticking = false;
        });
        ticking = true;
      }
    };
    document.addEventListener('scroll', onScroll, { passive: true });
  }

  // -------------------------------------------------------------------------
  // 2. Desktop mega menu
  // -------------------------------------------------------------------------
  const shopNav = document.querySelector('[data-shop-nav]');
  if (shopNav) {
    const trigger = shopNav.querySelector('[data-mega-trigger]');
    const menu = shopNav.querySelector('[data-megamenu]');
    const CLOSE_DELAY = 200;
    let closeTimer = null;

    const openMenu = () => {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      shopNav.setAttribute('data-open', 'true');
      trigger.setAttribute('aria-expanded', 'true');
      menu.removeAttribute('hidden');
    };

    const closeMenuNow = () => {
      shopNav.setAttribute('data-open', 'false');
      trigger.setAttribute('aria-expanded', 'false');
      menu.setAttribute('hidden', '');
    };

    const closeMenuDelayed = () => {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(closeMenuNow, CLOSE_DELAY);
    };

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

    shopNav.addEventListener('mouseenter', () => { if (isDesktop()) openMenu(); });
    shopNav.addEventListener('mouseleave', () => { if (isDesktop()) closeMenuDelayed(); });
    menu.addEventListener('mouseenter', () => { if (isDesktop()) openMenu(); });
    menu.addEventListener('mouseleave', () => { if (isDesktop()) closeMenuDelayed(); });

    // Keyboard: ENTER/SPACE on trigger toggles, ESC closes, click on trigger also toggles
    trigger.addEventListener('click', function (e) {
      // On desktop only; on mobile, drawer takes over
      if (!isDesktop()) return;
      // If trigger has a real href, prevent the navigation on first click — toggle menu instead
      // (Second activation via keyboard ENTER follows the link.)
      if (shopNav.getAttribute('data-open') === 'true') {
        closeMenuNow();
      } else {
        e.preventDefault();
        openMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && shopNav.getAttribute('data-open') === 'true') {
        closeMenuNow();
        trigger.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (!shopNav.contains(e.target) && shopNav.getAttribute('data-open') === 'true') {
        closeMenuNow();
      }
    });
  }

  // -------------------------------------------------------------------------
  // 3. Mobile drawer
  // -------------------------------------------------------------------------
  const drawer = document.querySelector('[data-drawer]');
  const drawerOpen = document.querySelector('[data-drawer-open]');
  const drawerClose = document.querySelector('[data-drawer-close]');

  if (drawer && drawerOpen) {
    let savedScrollY = 0;
    let lastFocused = null;

    const lockBody = () => {
      savedScrollY = window.scrollY;
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.classList.add('rr-drawer-open');
    };

    const unlockBody = () => {
      document.body.classList.remove('rr-drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, savedScrollY);
    };

    const openDrawer = () => {
      lastFocused = document.activeElement;
      drawer.removeAttribute('hidden');
      // Force a reflow so the slide animation runs from the initial transform
      // eslint-disable-next-line no-unused-expressions
      drawer.offsetHeight;
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      drawerOpen.setAttribute('aria-expanded', 'true');
      lockBody();
      // Focus the close button so screen readers/keyboards land somewhere sensible
      if (drawerClose) drawerClose.focus();
    };

    const closeDrawer = () => {
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      drawerOpen.setAttribute('aria-expanded', 'false');
      // After the slide-out, remove from layout
      const teardown = () => {
        drawer.setAttribute('hidden', '');
        unlockBody();
        if (lastFocused && typeof lastFocused.focus === 'function') {
          lastFocused.focus();
        }
      };
      if (reduceMotion) teardown();
      else setTimeout(teardown, 260);
    };

    drawerOpen.addEventListener('click', openDrawer);
    if (drawerClose) drawerClose.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') {
        closeDrawer();
      }
    });

    // Swipe-right-to-close
    let touchStartX = 0;
    let touchStartY = 0;
    let touchMoved = false;
    const SWIPE_THRESHOLD = 60;

    drawer.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchMoved = false;
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      // Only count as a horizontal swipe if X movement dominates Y
      if (Math.abs(dx) > Math.abs(dy)) touchMoved = true;
    }, { passive: true });

    drawer.addEventListener('touchend', (e) => {
      if (!touchMoved || e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (dx > SWIPE_THRESHOLD) closeDrawer();
    }, { passive: true });

    // Close drawer when a link inside is tapped (so navigation feels right)
    drawer.querySelectorAll('a').forEach((a) => {
      a.addEventListener('click', () => {
        // Allow the navigation to happen; close so it's not still mounted on return-via-back
        closeDrawer();
      });
    });
  }

  // -------------------------------------------------------------------------
  // 4. Drawer "Shop" accordion
  // -------------------------------------------------------------------------
  const drawerShop = document.querySelector('[data-drawer-shop]');
  if (drawerShop) {
    const toggle = drawerShop.querySelector('.rr-drawer__link--toggle');
    const sub = drawerShop.querySelector('.rr-drawer__sub');
    if (toggle && sub) {
      toggle.addEventListener('click', () => {
        const isOpen = drawerShop.getAttribute('data-open') === 'true';
        if (isOpen) {
          drawerShop.setAttribute('data-open', 'false');
          toggle.setAttribute('aria-expanded', 'false');
          sub.setAttribute('hidden', '');
        } else {
          drawerShop.setAttribute('data-open', 'true');
          toggle.setAttribute('aria-expanded', 'true');
          sub.removeAttribute('hidden');
        }
      });
    }
  }
})();
